import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { PaletteMode } from '@mui/material';
import { SYSTEM_PERMISSIONS } from '../constants/permissions.constants';
import { useAppSelector } from '../redux/hook';
import {
  selectAuthLoading,
  selectIsAuthenticated,
  selectUserPermissions,
} from '../redux/slices/auth.slice';
import { permissionsService } from '../services/permissions.service';

export const APP_COLOR_MODE_STORAGE_KEY = 'app-color-mode';

type ColorModeContextValue = {
  mode: PaletteMode;
  setMode: (mode: PaletteMode) => void;
  toggleColorMode: () => void;
};

const ColorModeContext = createContext<ColorModeContextValue | null>(null);

function readStoredMode(): PaletteMode {
  try {
    const v = localStorage.getItem(APP_COLOR_MODE_STORAGE_KEY);
    if (v === 'dark' || v === 'light') {
      return v;
    }
  } catch {
    // ignore
  }
  return 'light';
}

function persistColorModeToStorage(next: PaletteMode): void {
  try {
    localStorage.setItem(APP_COLOR_MODE_STORAGE_KEY, next);
  } catch {
    // ignore
  }
}

export const ColorModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const authLoading = useAppSelector(selectAuthLoading);
  const userPermissions = useAppSelector(selectUserPermissions);

  const canSelectTheme = useMemo(
    () => permissionsService.can(userPermissions, SYSTEM_PERMISSIONS.SELECT_THEME),
    [userPermissions],
  );

  const [mode, setModeState] = useState<PaletteMode>(() => readStoredMode());

  const setMode = useCallback(
    (next: PaletteMode) => {
      if (!canSelectTheme) {
        setModeState('light');
        return;
      }
      setModeState(next);
      persistColorModeToStorage(next);
    },
    [canSelectTheme],
  );

  const toggleColorMode = useCallback(() => {
    if (!canSelectTheme) return;
    setMode(mode === 'dark' ? 'light' : 'dark');
  }, [mode, setMode, canSelectTheme]);

  useEffect(() => {
    if (!isAuthenticated) {
      // Login/public pages always render light; keep stored preference for next session.
      setModeState('light');
      return;
    }
    if (authLoading) return;
    if (!canSelectTheme) {
      setModeState('light');
      return;
    }
    setModeState(readStoredMode());
  }, [isAuthenticated, authLoading, canSelectTheme]);

  const value = useMemo(
    () => ({ mode, setMode, toggleColorMode }),
    [mode, setMode, toggleColorMode],
  );

  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
};

export function useColorMode(): ColorModeContextValue {
  const ctx = useContext(ColorModeContext);
  if (!ctx) {
    throw new Error('useColorMode must be used within ColorModeProvider');
  }
  return ctx;
}
